import { Divider } from 'antd';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactHotkeys from 'react-hot-keys';
import styled, { css } from 'styled-components';

import { MenuArrowDownSvg } from '../../../../assets/icons';
import theme from '../../../../assets/theme';
import { LicenseCoarseLevels, UserAccessBadge } from '../../../../hooks/useAccessBadge';
import { useOnOutsideClick } from '../../../../hooks/useOnOutsideClick';
import { PaywallMetadata } from '../../../../hooks/usePaywallPrompt';
import { IconWrapper } from '../../../_atoms/IconWrapper';
import { DropdownOption } from '../test-prep-tab/TestPrep.Utils';
import { TestprepPlanIcon } from '../test-prep-tab/TestPrepTab';

export const FloatingSelect = ({
  onSelect,
  options,
  value,
  placeholder,
  width,
  label,
  disabled,
  darkMode,
  style,
}: {
  value: string | undefined;
  options: {
    displayName: string;
    value: string;
    disabled?: boolean;
    icon?: JSX.Element;
    displayDivider?: boolean;
  }[];
  onSelect: (value: string) => void;
  placeholder?: string | React.ReactNode;
  width?: string;
  label?: string;
  darkMode?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
}) => {
  const [showDropdown, setShowDropdown] = useState(false);

  const handleEscapePress = () => {
    setShowDropdown(false);
  };

  const handleSelect = (value: string, disabled?: boolean) => {
    if (disabled) return;
    setShowDropdown(false);
    onSelect(value);
  };

  const selectedOption = useMemo(() => {
    return options.find(o => o.value === value);
  }, [options, value]);

  const wrapperRef = useRef<HTMLDivElement>(null);

  useOnOutsideClick(wrapperRef, handleEscapePress);

  return (
    <Wrapper ref={wrapperRef} $width={width}>
      {label && (
        <label
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: darkMode ? theme.colors.white : theme.colors.black,
          }}
        >
          {label}
        </label>
      )}
      <FakeInputV2
        $darkMode={darkMode}
        style={
          style
            ? { ...style, cursor: disabled ? 'not-allowed' : 'pointer' }
            : { cursor: disabled ? 'not-allowed' : 'pointer' }
        }
        onClick={() => {
          if (disabled) return;
          setShowDropdown(!showDropdown);
        }}
      >
        <div style={{ overflow: 'hidden' }}>
          <span style={{ whiteSpace: 'nowrap' }}>
            {selectedOption?.displayName || placeholder || 'select'}
          </span>
        </div>
        {!darkMode && (
          <IconWrapper height="12px" width="12px">
            <MenuArrowDownSvg />
          </IconWrapper>
        )}
      </FakeInputV2>
      <div style={{ position: 'relative', width: '100%' }}>
        <AnimatePresence>
          {showDropdown && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{
                position: 'absolute',
                top: 2,
                width: darkMode ? '210px' : '100%',
                left: 0,
                background: theme.colors.white,
                zIndex: 9999,
                boxShadow: theme.effects.defaultShadow,
                borderRadius: 5,
                maxHeight: 250,
                overflowY: 'auto',
              }}
            >
              {options.map(option => (
                <>
                  {option.displayDivider && (
                    <div style={{ height: 1, background: theme.colors.grey }} />
                  )}
                  <OptionContainer
                    $divider={option.displayDivider || false}
                    key={option.value}
                    $selected={option.value === value}
                    $disabled={option.disabled}
                    onClick={() => handleSelect(option.value, option.disabled)}
                  >
                    {option.icon && (
                      <IconWrapper style={{ marginRight: '8px' }}>
                        {option.icon}
                      </IconWrapper>
                    )}
                    {option.displayName}
                  </OptionContainer>
                </>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <ReactHotkeys keyName="Escape" onKeyUp={handleEscapePress} />
    </Wrapper>
  );
};
export const TestPrepFloatingSelect = ({
  onSelect,
  options,
  value,
  placeholder,
  width,
  label,
  disabled,
  darkMode,
  style,
  displayCantFindOption,
  handleShowRequestTestModal,
  promptPaywall,
  userAccessBadge,
  isReady,
  testRequest,
}: {
  value: string | undefined;
  options: {
    displayName: string;
    value: string;
    disabled?: boolean;
    icon?: JSX.Element;
    isEnterprise?: boolean;
    displayDivider?: boolean;
  }[];
  onSelect: (value: string) => void;
  placeholder?: string | React.ReactNode;
  width?: string;
  label: string;
  darkMode?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
  displayCantFindOption?: boolean;
  handleShowRequestTestModal?: (location: string) => void;
  promptPaywall?: (metadata?: PaywallMetadata | undefined) => void;
  userAccessBadge?: UserAccessBadge | undefined;
  isReady?: boolean;
  testRequest?: React.ReactNode;
}) => {
  const [showDropdown, setShowDropdown] = useState(false);

  const handleEscapePress = () => {
    setShowDropdown(false);
  };

  const handleSelect = (selectedValue: string, disabled?: boolean) => {
    if (disabled) return;
    //if option is aleady selected, do nothing
    if (selectedValue === value) {
      setShowDropdown(false);
      return;
    }

    setShowDropdown(false);
    onSelect(selectedValue);
  };

  const selectedOption = useMemo(() => {
    return options.find(o => o.value === value);
  }, [options, value]);

  const wrapperRef = useRef<HTMLDivElement>(null);

  useOnOutsideClick(wrapperRef, handleEscapePress);

  return (
    <Wrapper ref={wrapperRef} $width={width}>
      {label && (
        <label
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: darkMode ? theme.colors.white : theme.colors.black,
          }}
        >
          {label}
        </label>
      )}
      <FakeInputV2
        $darkMode={darkMode}
        style={
          style
            ? { cursor: disabled ? 'not-allowed' : 'pointer', ...style }
            : { cursor: disabled ? 'not-allowed' : 'pointer' }
        }
        onClick={() => {
          if (disabled) return;
          setShowDropdown(!showDropdown);
        }}
      >
        <div style={{ overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
          <span style={{ whiteSpace: 'nowrap' }}>
            {selectedOption?.displayName || placeholder || 'Select'}
          </span>
          {selectedOption?.isEnterprise && (
            <IconWrapper style={{ marginLeft: '8px' }}>
              {
                <TestprepPlanIcon
                  isPremiumPlanGenerator={false}
                  isEnterprisePlanGenerator={selectedOption.isEnterprise}
                  hasAccessToGenerator={
                    userAccessBadge?.license.coarseLevel ===
                    LicenseCoarseLevels.enterprise
                  }
                />
              }
            </IconWrapper>
          )}
        </div>
        {!darkMode && (
          <IconWrapper height="12px" width="12px">
            <MenuArrowDownSvg />
          </IconWrapper>
        )}
      </FakeInputV2>
      <div style={{ position: 'relative', width: '100%' }}>
        <AnimatePresence>
          {showDropdown && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{
                position: 'absolute',
                top: 2,
                width: darkMode ? '210px' : '100%',
                left: 0,
                background: theme.colors.white,
                zIndex: 9999,
                boxShadow: theme.effects.defaultShadow,
                borderRadius: 5,
                maxHeight: 250,
                overflowY: 'auto',
              }}
            >
              {options.map((option, i) => (
                <>
                  {option.displayDivider && (
                    <div key={i} style={{ height: 1, background: theme.colors.grey }} />
                  )}
                  <OptionContainer
                    $divider={option.displayDivider || false}
                    key={option.value}
                    $selected={option.value === value}
                    $disabled={option.disabled}
                    onClick={() => {
                      if (!isReady && promptPaywall && userAccessBadge) return;
                      if (
                        option.isEnterprise &&
                        promptPaywall &&
                        userAccessBadge?.license.coarseLevel !==
                          LicenseCoarseLevels.enterprise
                      ) {
                        promptPaywall({
                          label: 'Upgrade account',
                          description: `School plan passage type selected`,
                        });
                        return;
                      }
                      handleSelect(option.value, option.disabled);
                    }}
                  >
                    {option.displayName}
                    {option.isEnterprise && (
                      <IconWrapper style={{ marginRight: '8px', marginLeft: 10 }}>
                        {
                          <TestprepPlanIcon
                            isPremiumPlanGenerator={false}
                            isEnterprisePlanGenerator={option.isEnterprise}
                            hasAccessToGenerator={
                              userAccessBadge?.license.coarseLevel ===
                              LicenseCoarseLevels.enterprise
                            }
                          />
                        }
                      </IconWrapper>
                    )}
                  </OptionContainer>
                </>
              ))}
              {displayCantFindOption && handleShowRequestTestModal && (
                <>
                  <Divider
                    style={{
                      minWidth: '100%',
                      width: '100%',
                      margin: '0px 0px 10px 0px',
                    }}
                  />
                  <div
                    style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      margin: '10px 15px',
                    }}
                    onClick={() => {
                      handleShowRequestTestModal(label);
                    }}
                  >
                    {testRequest}
                  </div>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <ReactHotkeys keyName="Escape" onKeyUp={handleEscapePress} />
    </Wrapper>
  );
};

export const CascadingFloatingSelect = ({
  onSelect,
  options,
  value,
  placeholder,
  width,
  label,
  disabled,
  darkMode,
  setSelectedCategory,
  selectedCategory,
  displayCantFindOption,
  handleShowRequestTestModal,
}: {
  value: { value: string; subValue?: string };
  options: { option: DropdownOption; subOptions: DropdownOption[] }[];
  onSelect: (value: string, subValue?: string) => void;
  placeholder?: string | React.ReactNode;
  width?: string;
  label: string;
  darkMode?: boolean;
  disabled?: boolean;
  setSelectedCategory: (value: string | undefined) => void;
  selectedCategory: string | undefined;
  displayCantFindOption?: boolean;
  handleShowRequestTestModal?: (location: string) => void;
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedMainOptionRef = useRef<HTMLDivElement | null>(null);

  const handleEscapePress = () => {
    setShowDropdown(false);
  };

  const handleSelect = (value: string, disabled: boolean, mainCategory: string) => {
    if (disabled) return;
    setShowDropdown(false);
    onSelect(mainCategory, value);
  };

  const wrapperRef = useRef<HTMLDivElement>(null);

  useOnOutsideClick(wrapperRef, handleEscapePress);

  const selectedOptionDisplayName = useMemo(() => {
    const selectedOption = options.find(o => o.option.value === value.value);
    if (selectedOption) {
      const selectedSubOption = selectedOption.subOptions.find(
        o => o.value === value.subValue,
      );
      if (selectedSubOption) {
        return `${selectedOption.option.displayName} - ${selectedSubOption.displayName}`;
      }
      return selectedOption.option.displayName;
    }
    return undefined;
  }, [options, value]);

  useEffect(() => {
    if (showDropdown && selectedMainOptionRef.current) {
      selectedMainOptionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [showDropdown, selectedCategory, selectedMainOptionRef]);

  return (
    <Wrapper ref={wrapperRef} $width={width}>
      {label && (
        <label
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: darkMode ? theme.colors.white : theme.colors.black,
          }}
        >
          {label}
        </label>
      )}
      <FakeInputV2
        $darkMode={darkMode}
        style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
        onClick={() => {
          if (disabled) return;
          setShowDropdown(!showDropdown);
        }}
      >
        <div style={{ overflow: 'hidden' }}>
          <span style={{ whiteSpace: 'nowrap' }}>
            {selectedOptionDisplayName || placeholder || 'Select'}
          </span>
        </div>
        {!darkMode && (
          <IconWrapper height="12px" width="12px">
            <MenuArrowDownSvg />
          </IconWrapper>
        )}
      </FakeInputV2>
      <div style={{ position: 'relative', width: '100%' }}>
        <AnimatePresence>
          {showDropdown && (
            <motion.div
              ref={dropdownRef}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{
                position: 'absolute',
                top: 2,
                width: darkMode ? '210px' : '100%',
                left: 0,
                background: theme.colors.white,
                zIndex: 9999,
                boxShadow: theme.effects.defaultShadow,
                borderRadius: 5,
                maxHeight: 250,
                overflowY: 'auto',
              }}
            >
              {options.map((option, i) => {
                const isCurrentlySelectedMainOption = option.option.value === value.value;
                const isNewlySelectedMainOption =
                  selectedCategory === option.option.value;

                return (
                  <>
                    {option.option.displayDivider && (
                      <div key={i} style={{ height: 1, background: theme.colors.grey }} />
                    )}
                    <MainOptionContainer
                      ref={
                        isNewlySelectedMainOption
                          ? selectedMainOptionRef
                          : isCurrentlySelectedMainOption
                          ? selectedMainOptionRef
                          : null
                      }
                      $divider={false}
                      key={option.option.value}
                      $selected={option.option.value === value.value}
                      $disabled={false}
                      style={{ fontWeight: 'bold' }}
                      onClick={() => {
                        setSelectedCategory(option.option.value);
                      }}
                    >
                      {option.option.displayName}
                    </MainOptionContainer>
                    {(option.option.value === value.value ||
                      option.option.value === selectedCategory) &&
                      option.subOptions && (
                        <div>
                          {option.subOptions.map(subOption => (
                            <OptionContainer
                              style={{ padding: '10px 30px' }}
                              key={subOption.value}
                              $selected={subOption.value === value.subValue}
                              $divider={false}
                              $disabled={false}
                              onClick={() => {
                                handleSelect(subOption.value, false, option.option.value);
                              }}
                            >
                              {subOption.displayName}
                            </OptionContainer>
                          ))}
                        </div>
                      )}
                  </>
                );
              })}
              {displayCantFindOption && handleShowRequestTestModal && (
                <>
                  <Divider
                    style={{
                      minWidth: '100%',
                      width: '100%',
                      margin: '0px 0px 10px 0px',
                    }}
                  />
                  <div
                    style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      margin: '10px 15px',
                    }}
                    onClick={() => {
                      handleShowRequestTestModal(label);
                    }}
                  >
                    <span style={{ fontWeight: 'normal', color: theme.colors.greyDark }}>
                      Don&apos;t see your state?{' '}
                      <i
                        style={{
                          color: theme.colors.greyDark,
                          textDecoration: 'underline',
                        }}
                      >
                        Request your state here.
                      </i>
                    </span>
                  </div>
                </>
              )}
            </motion.div>
          )}

          <ReactHotkeys keyName="Escape" onKeyUp={handleEscapePress} />
        </AnimatePresence>
      </div>
    </Wrapper>
  );
};

const OptionContainer = styled.div<{
  $selected?: boolean;
  $disabled?: boolean;
  $divider: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  padding: 10px 15px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
  width: 100%;
  ${({ $divider }) =>
    $divider &&
    css`
      background: #fafafa;
    `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      cursor: not-allowed;
      color: ${theme.colors.grey};
    `}

  &:hover {
    background: ${({ $disabled }) => ($disabled ? 'none' : theme.colors.greyLight)};
  }
  user-select: none;

  ${({ $selected }) =>
    $selected &&
    css`
      background: #f6ece0;
      font-weight: bold;
    `}
`;

const MainOptionContainer = styled.div<{
  $selected?: boolean;
  $disabled?: boolean;
  $divider: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  padding: 10px 15px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
  width: 100%;
  font-size: 16px;
  color: ${theme.colors.black};
  ${({ $divider }) =>
    $divider &&
    css`
      background: #fafafa;
    `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      cursor: not-allowed;
      color: ${theme.colors.grey};
    `}

  &:hover {
    background: ${({ $disabled }) => ($disabled ? 'none' : '#f5dcbf')};
  }
  user-select: none;

  ${({ $selected }) =>
    $selected &&
    css`
      background: #f5dcbf;
      font-weight: bold;
    `}
`;

const FakeInputV2 = styled.div<{ $darkMode?: boolean }>`
  width: 100%;
  min-width: ${({ $darkMode }) => ($darkMode ? 20 : 70)}px;
  display: ${({ $darkMode }) => ($darkMode ? 'flex' : 'grid')};
  justify-content: space-between;
  grid-template-columns: auto 15px;
  align-items: center;
  line-height: 22px;
  font-size: 16px;
  outline: none;
  gap: 5px;

  border-radius: 5px;
  padding: 10px 15px;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  background: ${({ $darkMode }) => ($darkMode ? 'none' : theme.colors.white)};
  color: #5b5b5a;
`;

const Wrapper = styled.div<{ $width?: string }>`
  cursor: pointer;
  position: relative;
  ${({ $width }) =>
    $width &&
    css`
      width: ${$width};
    `}
`;
